import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, MapPin } from "lucide-react";

import { cn } from "../../lib/utils";
import { Place, WithId, normalizeText, searchPlaces } from "../../lib/pricing";
import { QuoteEndpointState } from "../../hooks/useQuote";
import { Tone, tones } from "./tones";

interface Props {
  id?: string;
  value: QuoteEndpointState;
  onChange: (value: QuoteEndpointState) => void;
  places: WithId<Place>[];
  zoneNames: Record<string, string>;
  zoneOrder: string[];
  placeholder?: string;
  tone?: Tone;
  autoFocus?: boolean;
}

/**
 * Campo de busca de locais com lista agrupada por região. Sem dependências
 * externas: input + lista posicionada. Texto livre é permitido (vira
 * "sob consulta" no motor de preço).
 */
export default function PlaceCombobox({
  id,
  value,
  onChange,
  places,
  zoneNames,
  zoneOrder,
  placeholder,
  tone = "light",
  autoFocus,
}: Props) {
  const { t } = useTranslation();
  const c = tones[tone];
  const generatedId = useId();
  const inputId = id || `place-${generatedId}`;
  const listId = `${inputId}-list`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => searchPlaces(value.text, places, zoneNames),
    [value.text, places, zoneNames]
  );

  const groups = useMemo(() => {
    const byZone = new Map<string, WithId<Place>[]>();
    filtered.forEach((place) => {
      const list = byZone.get(place.zoneId) || [];
      list.push(place);
      byZone.set(place.zoneId, list);
    });
    const ordered = zoneOrder.filter((zoneId) => byZone.has(zoneId));
    byZone.forEach((_, zoneId) => {
      if (!ordered.includes(zoneId)) ordered.push(zoneId);
    });
    return ordered.map((zoneId) => ({
      zoneId,
      name: zoneNames[zoneId] || "",
      places: byZone.get(zoneId) || [],
    }));
  }, [filtered, zoneOrder, zoneNames]);

  const flat = useMemo(() => groups.flatMap((g) => g.places), [groups]);
  const hasExact = flat.some((p) => normalizeText(p.name) === normalizeText(value.text));
  const showFreeText = value.text.trim().length > 0 && !hasExact;
  const optionCount = flat.length + (showFreeText ? 1 : 0);

  useEffect(() => {
    setActiveIndex(0);
  }, [value.text]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const select = (place: WithId<Place>) => {
    onChange({ placeId: place.id, text: place.name });
    setOpen(false);
  };

  const keepFreeText = () => {
    onChange({ placeId: undefined, text: value.text.trim() });
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(optionCount - 1, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (event.key === "Enter") {
      if (!open) return;
      event.preventDefault();
      if (activeIndex < flat.length) select(flat[activeIndex]);
      else keepFreeText();
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const selectedPlace = value.placeId ? places.find((p) => p.id === value.placeId) : undefined;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin
          className={cn("pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", c.muted)}
        />
        <input
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          autoFocus={autoFocus}
          maxLength={80}
          value={value.text}
          placeholder={placeholder}
          className={cn(c.input, "min-w-0 pl-9 pr-9")}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            onChange({ placeId: undefined, text: e.target.value });
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? t("quote.closeList") : t("quote.openList")}
          className={cn("absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1", c.muted)}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronDown className={cn("h-4 w-4 transition", open && "rotate-180")} />
        </button>
      </div>

      {selectedPlace && !open && (
        <p className={cn("mt-1 text-xs", c.muted)}>{zoneNames[selectedPlace.zoneId]}</p>
      )}

      {open && (
        <div
          id={listId}
          role="listbox"
          className={cn(
            "absolute left-0 right-0 z-40 mt-1 max-h-72 overflow-y-auto rounded-xl border py-1",
            c.dropdown
          )}
        >
          {groups.map((group) => (
            <div key={group.zoneId}>
              <p className={cn("px-3 py-1 text-[10px] font-bold uppercase tracking-wider", c.group)}>
                {group.name}
              </p>
              {group.places.map((place) => {
                const index = flat.indexOf(place);
                return (
                  <button
                    key={place.id}
                    type="button"
                    role="option"
                    aria-selected={value.placeId === place.id}
                    data-active={index === activeIndex}
                    className={cn("flex w-full items-center gap-2 px-3 py-2 text-left text-sm", c.option)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => select(place)}
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    <span className="truncate">{place.name}</span>
                  </button>
                );
              })}
            </div>
          ))}

          {showFreeText && (
            <button
              type="button"
              role="option"
              aria-selected={false}
              data-active={activeIndex === flat.length}
              className={cn("flex w-full flex-col items-start px-3 py-2 text-left text-sm", c.option)}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(flat.length)}
              onClick={keepFreeText}
            >
              <span>{t("quote.useText", { text: value.text.trim() })}</span>
              <span className={cn("text-xs", c.optionMuted)}>{t("quote.freeTextHint")}</span>
            </button>
          )}

          {optionCount === 0 && (
            <p className={cn("px-3 py-2 text-sm", c.optionMuted)}>{t("quote.noResults")}</p>
          )}
        </div>
      )}
    </div>
  );
}
